<script>
import Layout from '@/components/layout/app-layout'
import appConfig from '@/app.config'
import PageHeader from '@/components/layout/page-header'
import api from '@/helpers/api'
import CustomViews from './custom-views'
import ShippingCard from './shared/shipping-card.vue'
import { vue3dLoader } from 'vue-3d-loader'

/**
 * My Orders - order detail
 */
export default {
  name: 'sales.web-orders.view',
  page: {
    title: 'My Orders - View',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  components: {
    Layout,
    PageHeader,
    ShippingCard,
    vue3dLoader
  },
  props: {
    orderId: { type: String, required: true }
  },
  data: () => ({
    isLoading: false,
    isCancelling: false,
    fileUrl: '',
    isFileLoading: true,
    fileLoadProgress: 0,
    lights: [
      {
        type: 'point',
        color: '#0095f5',
        intensity: 1
      }
    ],
    webForm: {
      item: {},
      user: {},
      customer: {}
    }
  }),
  mounted () {
    this.onInit()
  },
  watch: {
    orderId: 'onInit'
  },
  methods: {
    async onInit () {
      this.isLoading = true
      try {
        const item = await api.webForms.fetch({
          id: this.orderId
        })
        this.webForm = {
          ...item
        }
        this.fileUrl = await this.getFileUrl(item.file_id)
      } finally {
        this.isLoading = false
      }
    },
    async downloadFile (id) {
      this.isLoading = true
      try {
        const url = await this.getFileUrl(id)
        window.open(url, '_blank', 'noreferrer')
      } finally {
        this.isLoading = false
      }
    },
    print () {
      window.print()
    },
    async getFileUrl (id) {
      if (!id) return null
      const { url } = await api.files.download_url({
        file_id: id
      })
      return url
    },
    onProcess (event) {
      this.fileLoadProgress = Math.floor((event.loaded / event.total) * 100)
      this.isFileLoading = this.fileLoadProgress < 100
    },
    async onCancelOrder () {
      const ok = await this.$bvModal.msgBoxConfirm(
        this.$t('order_details.cancel_confirm'),
        {
          title: this.$t('order_details.cancel_title'),
          size: 'md',
          okVariant: 'danger',
          cancelVariant: 'link',
          okTitle: this.$t('order_details.cancel_ok'),
          cancelTitle: this.$t('order_details.cancel_no'),
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )

      if (!ok) return

      this.isCancelling = true
      try {
        const item = await api.webForms.cancel({ id: this.orderId })
        if (item) {
          this.webForm = {
            ...this.webForm,
            ...item
          }
          this.$bvToast.toast(this.$t('order_details.cancel_success_body'), {
            title: this.$t('order_details.cancel_success'),
            solid: true,
            toaster: 'b-toaster-top-center',
            variant: 'success',
            autoHideDelay: 2500,
            appendToast: true
          })
        } else {
          throw new Error('cancel failed')
        }
      } catch (err) {
        this.$bvToast.toast(this.$t('order_details.cancel_error'), {
          title: this.$t('order_details.cancel_title'),
          solid: true,
          toaster: 'b-toaster-top-center',
          variant: 'danger',
          autoHideDelay: 3000,
          appendToast: true
        })
        await this.onInit()
      } finally {
        this.isCancelling = false
      }
    }
  },
  computed: {
    canCancel () {
      // Cancel is hidden until status updates are supported for portal users.
      return false
    },
    customView () {
      const { type } = this.webForm
      if (
        Object.prototype.hasOwnProperty.call(CustomViews, type) &&
        CustomViews[type].component
      ) {
        return CustomViews[type].component
      }
      return null
    },
    breadcrumbs () {
      return [
        {
          text: this.$t('my orders'),
          to: { name: 'sales.my-orders' }
        },
        {
          text: this.webForm.item
            ? `${this.webForm.item.short_description}`
            : this.$t('order_details.loading'),
          active: true
        }
      ]
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader
      :items="breadcrumbs"
      :title="$t('order_details.title')"
      class="d-print-none"
    />

    <div v-if="isLoading && !webForm.id" class="text-muted small py-4">
      {{ $t('order_details.loading') }}
    </div>

    <div v-else class="order-detail">
      <div class="order-detail__main">
        <div class="order-detail__card">
          <div
            class="order-detail__identity d-flex justify-content-between align-items-start"
          >
            <div
              class="d-flex align-items-start"
              :title="webForm.item.extra_description"
              v-b-tooltip.right.hover
            >
              <div class="order-detail__icon">
                <i class="fab fa-wpforms" />
              </div>
              <div>
                <h5 class="order-detail__title mb-0">
                  {{ webForm.item.short_description }}
                </h5>
                <span class="order-detail__code">{{ webForm.item.code }}</span>
              </div>
            </div>
            <app-status-badge :status="webForm.status" />
          </div>

          <div class="order-detail__facts">
            <div class="order-detail__fact">
              <span class="order-detail__fact-label">{{ $t('reference') }}</span>
              <span class="order-detail__fact-value">
                {{ webForm.reference || $t('no reference') }}
              </span>
            </div>
            <div class="order-detail__fact">
              <span class="order-detail__fact-label">{{ $t('quantity') }}</span>
              <span class="order-detail__fact-value">{{
                webForm.quantity
              }}</span>
            </div>
          </div>

          <div v-if="!!webForm.file" class="order-detail__section">
            <h6 class="order-detail__section-title">{{ $t('file') }}</h6>
            <b-btn
              variant="link"
              class="order-detail__file-link p-0"
              @click="downloadFile(webForm.file.id)"
            >
              <i class="mdi mdi-file-check-outline"></i>
              {{ webForm.file.file_name }}
              <span class="small mx-1"
                >({{ webForm.file.file_size | prettyBytes }})</span
              >
              <i class="mdi mdi-download ml-2"></i>
            </b-btn>

            <div class="order-detail__viewer mt-2">
              <vue3dLoader
                :parallelLoad="true"
                :filePath="fileUrl"
                fileType="stl"
                :enableAxesHelper="true"
                :axesHelperSize="500"
                :enableDamping="true"
                :dampingFactor="0.05"
                :height="300"
                :controlsOptions="{
                  enablePan: false,
                  enableZoom: false
                }"
                :pointLightFollowCamera="true"
                :lights="lights"
                @process="onProcess"
                backgroundColor="#FFF"
                class="p-0 m-0"
              />
              <b-progress
                v-show="fileLoadProgress < 100"
                :value="fileLoadProgress"
                height="2px"
              ></b-progress>
            </div>
          </div>

          <div class="order-detail__section">
            <component
              :is="customView"
              v-model="webForm.form_data"
              v-if="!!customView"
            />
            <app-default-specs :specs="webForm.form_data" v-else />
          </div>

          <div v-if="!!webForm.comments" class="order-detail__section">
            <h6 class="order-detail__section-title">{{ $t('comments') }}</h6>
            <p class="order-detail__comments mb-0">{{ webForm.comments }}</p>
          </div>

          <div
            v-if="!!webForm.web_form_files && webForm.web_form_files.length > 0"
            class="order-detail__section"
          >
            <h6 class="order-detail__section-title">
              {{ $t('order_details.additional_files') }}
            </h6>
            <div
              v-for="wff in webForm.web_form_files"
              :key="wff.file.id"
              class="mb-1"
            >
              <b-btn
                variant="link"
                class="order-detail__file-link p-0"
                @click="downloadFile(wff.file.id)"
              >
                <i
                  v-if="wff.file.file_content_type === 'application/pdf'"
                  class="mdi mdi-file-pdf-outline"
                ></i>
                <i v-else class="mdi mdi-file-image-outline"></i>
                {{ wff.file.file_name }}
                <span class="small mx-1"
                  >({{ wff.file.file_size | prettyBytes }})</span
                >
                <i class="mdi mdi-download ml-2"></i>
              </b-btn>
            </div>
          </div>
        </div>
      </div>

      <aside class="order-detail__aside">
        <div class="order-detail__card d-print-none">
          <h6 class="order-detail__section-title mb-3">
            {{ $t('order_details.actions') }}
          </h6>
          <div class="order-detail__action-btns">
            <b-btn
              variant="primary"
              block
              class="order-detail__action-btn"
              :title="$t('order_details.print')"
              @click="print"
            >
              <i class="bx bx-printer mr-1" />
              {{ $t('order_details.print') }}
            </b-btn>
            <b-btn
              v-if="canCancel"
              variant="danger"
              block
              class="order-detail__action-btn"
              :disabled="isCancelling || isLoading"
              @click="onCancelOrder"
            >
              <i class="mdi mdi-cancel mr-1" />
              {{ $t('order_details.cancel') }}
            </b-btn>
          </div>
        </div>

        <div class="order-detail__card">
          <h6 class="order-detail__section-title mb-3">
            {{ $t('order_details.meta') }}
          </h6>
          <div class="order-detail__meta-row">
            <i class="far fa-user order-detail__meta-icon" />
            <div>
              <h6 class="order-detail__meta-label">
                {{ $t('order_details.user') }}
              </h6>
              <p class="order-detail__meta-value mb-0">
                {{ webForm.user.name }}
              </p>
              <p class="order-detail__meta-sub mb-0">
                {{ webForm.customer.company_name }}
              </p>
            </div>
          </div>
          <div class="order-detail__meta-row">
            <i class="far fa-clock order-detail__meta-icon" />
            <div>
              <h6 class="order-detail__meta-label">
                {{ $t('order_details.submitted') }}
              </h6>
              <p class="order-detail__meta-value mb-0">
                {{ webForm.submitted_at | moment('ddd D MMM YYYY') }}
              </p>
            </div>
          </div>
          <div class="order-detail__meta-row mb-0">
            <i class="far fa-clock order-detail__meta-icon" />
            <div>
              <h6 class="order-detail__meta-label">
                {{ $t('order_details.last_updated') }}
              </h6>
              <p class="order-detail__meta-value mb-0">
                {{ webForm.updated_at | moment('ddd D MMM YYYY - HH:mm') }}
              </p>
            </div>
          </div>
        </div>

        <shipping-card :shipping-address="webForm.shipping_address" />
      </aside>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
.order-detail {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 17.5rem;
  gap: 1rem;
  align-items: start;
}

.order-detail__main {
  min-width: 0;
}

.order-detail__aside {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}

.order-detail__card {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  padding: 1.25rem 1.35rem;
}

.order-detail__action-btns {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.order-detail__action-btn {
  font-weight: 600;
  padding: 0.65rem 1rem;
  font-size: 0.95rem;
}

.order-detail__identity {
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.order-detail__icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.65rem;
  background: rgba(148, 163, 184, 0.12);
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.85rem;
  flex-shrink: 0;
  font-size: 1.15rem;
}

.order-detail__title {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 600;
}

.order-detail__code {
  color: #94a3b8;
  font-size: 0.8rem;
}

.order-detail__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem 2rem;
  padding: 0.85rem 0 0.25rem;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
  margin-bottom: 0.5rem;
}

.order-detail__fact-label {
  display: block;
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 0.2rem;
}

.order-detail__fact-value {
  color: #334155;
  font-size: 0.95rem;
  font-weight: 500;
}

.order-detail__section {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
}

.order-detail__section-title {
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.order-detail__comments {
  color: #64748b;
  white-space: pre-wrap;
}

.order-detail__file-link {
  color: #475569 !important;
}

.order-detail__viewer {
  border-radius: 0.75rem;
  overflow: hidden;
  background: #fff;
}

.order-detail__meta-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.15rem;
}

.order-detail__meta-icon {
  color: #94a3b8;
  font-size: 1rem;
  margin-right: 0.85rem;
  margin-top: 0.15rem;
  width: 1rem;
  flex-shrink: 0;
}

.order-detail__meta-label {
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 0.2rem;
}

.order-detail__meta-value {
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 500;
}

.order-detail__meta-sub {
  color: #94a3b8;
  font-size: 0.8rem;
}

@media (max-width: 991.98px) {
  .order-detail {
    grid-template-columns: 1fr;
  }
}
</style>
